import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicBrowseShell from '../../components/public/PublicBrowseShell';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Input from '../../components/ui/Input';
import { playerService } from '../../services/playerService';

const formatPosition = (position) =>
  position ? position.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) : 'N/A';

const BrowsePlayersPage = () => {
  const [players, setPlayers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    document.title = 'Players - Tanzania Basketball Federation';
    loadPlayers();
  }, []);

  const loadPlayers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await playerService.getAll();
      setPlayers(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load players');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredPlayers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return players;
    return players.filter((player) =>
      [player?.userProfile?.fullName, player?.team?.name, player?.playerPosition]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [players, search]);

  return (
    <PublicBrowseShell title="Players" subtitle="Registered players across Tanzania Basketball Federation clubs">
      <div className="mb-6 max-w-md">
        <Input
          type="search"
          placeholder="Search players..."
          iconName="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Loader2" size={40} className="text-primary mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading players...</p>
        </div>
      ) : error ? (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} />
            <p className="font-medium">{error}</p>
          </div>
        </div>
      ) : filteredPlayers?.length === 0 ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Users" size={40} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Players Found</h3>
          <p className="text-muted-foreground">Try adjusting your search.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredPlayers.map((player) => (
            <Link
              key={player.id}
              to={`/browse-players/${player.id}`}
              className="bg-card rounded-lg border border-border card-shadow p-6 hover:shadow-lg transition-all hover:scale-[1.02]"
            >
              <div className="flex flex-col items-center text-center mb-4">
                <div className="w-20 h-20 rounded-full bg-emerald-600/10 flex items-center justify-center overflow-hidden mb-3">
                  {player?.photoUrl ? (
                    <Image src={player.photoUrl} alt={player?.userProfile?.fullName} className="w-full h-full object-cover" />
                  ) : (
                    <Icon name="User" size={32} className="text-emerald-600" />
                  )}
                </div>
                <h3 className="text-base font-semibold text-foreground truncate w-full">
                  {player?.userProfile?.fullName || 'Unknown Player'}
                </h3>
                <p className="text-sm text-muted-foreground truncate w-full">{player?.team?.name || 'Unassigned'}</p>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{formatPosition(player?.playerPosition)}</span>
                {player?.jerseyNumber != null && (
                  <span className="font-semibold text-primary">#{player.jerseyNumber}</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </PublicBrowseShell>
  );
};

export default BrowsePlayersPage;
